import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "javascript",
  name: "JavaScript",
  nameEn: "JavaScript",
  seo: {
    title: "JavaScriptを学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "JavaScriptはWeb開発に欠かせない必須言語です。フロントエンドからバックエンドまで、ゆめさくエンジニアで実践的なJavaScriptスキルを身につけましょう。",
    url: "/columns/javascript-programming-school",
  },
  hero: {
    title: "JavaScriptを学べる女性向けプログラミングスクール",
    englishTitle: "Learn JavaScript",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "JavaScript" },
  ],
  overview: {
    heading: "JavaScriptとは？",
    paragraphs: [
      "JavaScriptは、Webブラウザ上で動作する唯一のプログラミング言語です。1995年に誕生して以来、Web開発の中核を担い続けており、世界中のWebサイトの97%以上で使用されています。", // claims-ok 技術や職種の一般的な話
      "フロントエンド開発はもちろん、Node.jsの登場によりサーバーサイド開発も可能になりました。React、Vue、Angularなどのフレームワークと組み合わせることで、高度なWebアプリケーションを構築できます。",
      "ブラウザさえあればすぐにコードを実行できるため、環境構築のハードルが低く、学習を始めやすい言語でもあります。",
    ],
    useCases: [
      {
        label: "フロントエンド開発",
        description:
          "ユーザーが直接触れるWebページの動的な操作やアニメーション、フォームバリデーションなどを実装します。",
      },
      {
        label: "バックエンド開発（Node.js）",
        description:
          "Node.jsを使ってサーバーサイドのAPIやリアルタイム通信アプリケーションを構築できます。",
      },
      {
        label: "SPAアプリ開発",
        description:
          "ReactやVueを使って、ページ遷移なしで高速に動作するシングルページアプリケーションを開発します。",
      },
      {
        label: "モバイルアプリ開発",
        description:
          "React NativeやIonicを使えば、JavaScriptの知識でiOS・Androidアプリを同時に開発できます。",
      },
    ],
  },
  whyLearn: {
    heading: "JavaScriptを学ぶべき3つの理由",
    items: [
      {
        title: "Web開発のすべてに関わる必須言語",
        description:
          "Webサイトに動きをつけるにはJavaScriptが不可欠です。フロントエンドエンジニアを目指すなら必ず習得する言語であり、バックエンドやモバイルアプリにも応用できる汎用性の高さが魅力です。",
      },
      {
        title: "求人数が圧倒的に多い",
        description:
          "JavaScriptは国内外のエンジニア求人で常にトップクラスの需要があります。スタートアップから大企業まで幅広い企業で採用されており、就職・転職の選択肢が非常に豊富です。",
      },
      {
        title: "学習環境が充実し、すぐに始められる",
        description:
          "ブラウザの開発者ツールがあればすぐにコードを試せます。また、世界最大のエンジニアコミュニティがあるため、学習リソースやライブラリが豊富に揃っています。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでJavaScriptを学ぶ",
    paragraphs: [
      "ゆめさくエンジニアのカリキュラムでは、HTML/CSSでWebの基礎を学んだ後、JavaScriptでWebページに動的な機能を実装する方法を習得します。DOM操作からReactを使ったモダンなフロントエンド開発まで、段階的にスキルアップできます。",
      "実際のWebアプリケーション開発プロジェクトを通じて、チーム開発で求められる実践力を身につけます。",
    ],
    topics: [
      "JavaScript基礎文法・変数・関数・配列・オブジェクト",
      "DOM操作・イベントハンドリング",
      "非同期処理（Promise・async/await）",
      "ES6+のモダンな構文",
      "Node.jsの基礎とAPI開発",
      "実践プロジェクト：インタラクティブWebアプリ構築",
    ],
  },
  careerPaths: {
    heading: "JavaScriptを活かせるキャリア",
    items: [
      {
        title: "フロントエンドエンジニア",
        description:
          "Webサイトやアプリのユーザーインターフェースを構築する職種です。デザインをコードに落とし込み、ユーザー体験を形にします。React・Vue等のフレームワークスキルが求められます。",
        salary: "450〜750万円",
      },
      {
        title: "フルスタックエンジニア",
        description:
          "フロントエンドとバックエンドの両方を担当できるエンジニアです。JavaScriptとNode.jsで開発のすべてをカバーでき、スタートアップや少人数チームで特に重宝されます。",
        salary: "500〜900万円",
      },
      {
        title: "Webアプリケーションエンジニア",
        description:
          "ECサイト、SNS、業務システムなど、さまざまなWebアプリケーションの開発を担当します。ユーザーの課題を技術で解決するやりがいのある職種です。",
        salary: "400〜650万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "6〜10ヶ月（フレームワーク含む）",
        luagate: "3〜4ヶ月（体系的カリキュラム）",
      },
      {
        label: "挫折率",
        selfStudy: "非同期処理やフレームワークで挫折しやすい",
        luagate: "段階的な学習設計で理解を積み上げる",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "チュートリアルの模写にとどまりがち",
        luagate: "オリジナルWebアプリを企画から開発",
      },
      {
        label: "質問対応",
        selfStudy: "エラーの原因特定に何時間もかかる",
        luagate: "メンターがデバッグの考え方から指導",
      },
      {
        label: "就職サポート",
        selfStudy: "ポートフォリオの質がわからない",
        luagate: "企業目線でのポートフォリオレビュー付き",
      },
    ],
  },
  faq: [
    {
      question: "JavaScriptとJavaは同じ言語ですか？",
      answer:
        "名前は似ていますが、まったく別の言語です。JavaScriptはWebブラウザ上で動作する言語で、Javaはサーバーサイドやモバイルアプリ開発に使われる言語です。Web開発を目指すならJavaScriptを選びましょう。",
    },
    {
      question: "JavaScriptだけでエンジニアになれますか？",
      answer:
        "はい、JavaScriptはフロントエンドからバックエンド（Node.js）まで対応できるため、JavaScriptを中心としたスキルセットでエンジニアとして十分活躍できます。HTML/CSSとセットで学ぶのがおすすめです。",
    },
    {
      question: "JavaScriptとTypeScriptのどちらを先に学ぶべきですか？",
      answer:
        "まずはJavaScriptの基礎を学ぶことをおすすめします。TypeScriptはJavaScriptに型の仕組みを追加した言語なので、JavaScriptを理解してからTypeScriptに進むとスムーズに習得できます。",
    },
    {
      question: "フロントエンドとバックエンドの違いは何ですか？",
      answer:
        "フロントエンドはユーザーが目にする画面部分、バックエンドはデータの保存・処理などサーバー側の仕組みです。JavaScriptはどちらでも使えるため、両方を学んでから得意な方を選ぶこともできます。",
    },
  ],
  relatedLanguages: [
    { slug: "typescript", name: "TypeScript" },
    { slug: "react", name: "React" },
    { slug: "html-css", name: "HTML/CSS" },
    { slug: "python", name: "Python" },
  ],
};
